import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Check, Trash2, Camera } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import projectDataService from '../../services/projectDataService';
import aiService from '../../services/aiService';
import { apiService } from '../../services/api';
import { useCategoriesStore, Category } from '../../store/useCategoriesStore';
import { ImageUploadField } from '../common/ImageUploadField';
import { ImageUploadResult } from '../../services/imageUploadService';
import { screenshotService } from '../../services/screenshotService';
import ScreenshotUploadZone from './ScreenshotUploadZone';
import { FILL_GRADIENT } from '../../constants/gradients';

export interface ProjectFormData {
  title: string;
  categories: string[];
  description: string;
  playInstructions: string;
  iconUrl?: string;
  bannerUrl?: string;
  screenshots?: string[]; // Array of screenshot URLs
}

interface ProjectDetailsFormProps {
  projectId: string;
  code?: string; // Optional - only needed for AI generation
  initialData?: ProjectFormData; // Pre-populate form
  onSave?: (data: ProjectFormData) => Promise<void>; // Custom save handler
  onSaveWithoutValidation?: (data: ProjectFormData) => Promise<void>; // Save without validation
  onSectionComplete?: () => void;
  onClose?: () => void; // For modal usage
  showGenerateButton?: boolean; // Show AI generate button (default true)
  showSaveButton?: boolean; // Show additional Save button (default false)
  saveButtonText?: string; // Custom text for save button (default "Save")
  skipValidation?: boolean; // Skip validation on save (default false)
  hideButtons?: boolean; // Hide all buttons (for modal usage where parent controls buttons)
  silentSave?: boolean; // Suppress notifications and callbacks after save
  screenshots?: { all: string[]; selected: number[] };
  onScreenshotsChange?: (screenshots: { all: string[]; selected: number[] }) => void;
  isEditableInStudio?: boolean; // Whether this is a Studio project (true) or CLI project (false)
  onCaptureScreenshot?: () => void;
  isCapturingScreenshot?: boolean;
}

const ProjectDetailsForm = forwardRef(({ 
  projectId, 
  code, 
  initialData,
  onSave: customSave,
  onSaveWithoutValidation,
  onSectionComplete,
  onClose,
  showGenerateButton = true,
  showSaveButton = false,
  saveButtonText = 'Save',
  skipValidation = true, // Default to true - allow saving without validation
  hideButtons = false,
  silentSave = false,
  screenshots,
  onScreenshotsChange,
  isEditableInStudio = true, // Default to true (Studio project) for backward compatibility
  onCaptureScreenshot,
  isCapturingScreenshot = false
}: ProjectDetailsFormProps, ref) => {
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [description, setDescription] = useState('');
  const [playInstructions, setPlayInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | undefined>(undefined);
  const [bannerUrl, setBannerUrl] = useState<string | undefined>(undefined);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  
  // Studio projects: screenshots with selection tracking
  const [localScreenshots, setLocalScreenshots] = useState<{all: string[], selected: number[]}>({
    all: screenshots?.all || [],
    selected: screenshots?.selected || []
  });

  // Sync screenshots prop into local state when the parent loads them asynchronously
  const screenshotsPropRef = useRef(screenshots);
  useEffect(() => {
    const prev = screenshotsPropRef.current;
    screenshotsPropRef.current = screenshots;
    // Only overwrite local state if the prop changed AND local hasn't been touched independently
    if (screenshots && JSON.stringify(prev) !== JSON.stringify(screenshots)) {
      setLocalScreenshots({ all: screenshots.all || [], selected: screenshots.selected || [] });
    }
  }, [screenshots]);

  
  // CLI projects: simple screenshot array (all screenshots are included)
  const [cliScreenshots, setCliScreenshots] = useState<string[]>([]);
  
  const [previewScreenshot, setPreviewScreenshot] = useState<string | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  
  // Categories from store
  const { categories: availableCategories, fetchCategories, isLoading: loadingCategories } = useCategoriesStore();
  
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleGenerate,
    saveWithoutValidation: handleSaveWithoutValidation,
    save: handleSave,
    getFormData: () => {
      const screenshotsToReturn = isEditableInStudio
        ? localScreenshots.all.filter((_, i) => localScreenshots.selected.includes(i))
        : cliScreenshots;
      
      return {
        title,
        categories,
        description,
        playInstructions,
        iconUrl,
        bannerUrl,
        screenshots: screenshotsToReturn
      };
    }
  }), [title, categories, description, playInstructions, iconUrl, bannerUrl, localScreenshots, cliScreenshots, isEditableInStudio]);

  // Helper function to get icon component by name
  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.HelpCircle; // Fallback icon
  };

  // Helper function to find category by ID or display name (case-insensitive)
  const findCategoryByIdOrName = (searchTerm: string): Category | undefined => {
    if (!searchTerm) return undefined;
    const normalized = searchTerm.toLowerCase().replace(/\s+/g, '_');
    return availableCategories.find(cat => 
      cat.id === normalized || 
      cat.displayName.toLowerCase() === searchTerm.toLowerCase()
    );
  };

  // Helper function to convert category ID to display name
  const getCategoryDisplayName = (categoryIdOrName: string): string => {
    const category = findCategoryByIdOrName(categoryIdOrName);
    return category ? category.displayName : categoryIdOrName;
  };

  // Helper function to get category icon by ID or display name
  const getCategoryIcon = (categoryIdOrName: string) => {
    const category = findCategoryByIdOrName(categoryIdOrName);
    return category ? getIconComponent(category.icon) : null;
  };

  // Fetch categories from store
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Convert category IDs to display names after categories are loaded
  useEffect(() => {
    if (availableCategories.length > 0 && categories.length > 0) {
      // Check if categories contain IDs (lowercase with underscores or exact ID match)
      const needsConversion = categories.some(cat => {
        const found = findCategoryByIdOrName(cat);
        // If found by ID but current value doesn't match display name, needs conversion
        return found && cat !== found.displayName;
      });
      
      if (needsConversion) {
        const displayNames = categories.map(cat => getCategoryDisplayName(cat));
        setCategories(displayNames);
        console.log('✅ Converted category IDs to display names:', displayNames);
      }
    }
  }, [availableCategories, categories]);

  // Sync with parent screenshots or load from metadata
  useEffect(() => {
    if (isEditableInStudio) {
      // Studio project: use {all, selected} structure
      if (screenshots) {
        setLocalScreenshots(screenshots);
      } else {
        // If no screenshots prop provided (Studio modal case), load from metadata
        loadScreenshotsFromMetadata();
      }
    } else {
      // CLI project: load from initialData or game_content
      if (initialData?.screenshots) {
        setCliScreenshots(initialData.screenshots);
      } else {
        loadCliScreenshots();
      }
    }
  }, [screenshots, projectId, isEditableInStudio, initialData]);

  const loadScreenshotsFromMetadata = async () => {
    try {
      const data = await projectDataService.getProjectData(projectId);
      if (data?.metadata?.screenshots) {
        setLocalScreenshots(data.metadata.screenshots);
      }
    } catch (error) {
      console.error('Failed to load screenshots from metadata:', error);
    }
  };

  const loadCliScreenshots = async () => {
    try {
      const response = await apiService.get(`/games/${projectId}`);
      if (response.data.success && response.data.data?.content?.screenshot_urls) {
        setCliScreenshots(response.data.data.content.screenshot_urls);
      }
    } catch (error) {
      console.error('Failed to load CLI screenshots:', error);
    }
  };

  // Load existing project data or use initialData (only once)
  useEffect(() => {
    // Skip if already loaded initial data to prevent overwriting generated data
    if (hasLoadedInitialData) {
      return;
    }

    const loadProjectData = async () => {
      // If initialData provided, use it (modal mode)
      if (initialData) {
        setTitle(initialData.title || '');
        setCategories(initialData.categories || []);
        setDescription(initialData.description || '');
        setPlayInstructions(initialData.playInstructions || '');
        setIconUrl(initialData.iconUrl);
        setBannerUrl(initialData.bannerUrl);
        setLoadingData(false);
        setHasLoadedInitialData(true);
        console.log('✅ Loaded from initialData:', initialData);
        return;
      }

      // Otherwise load from server (standalone mode) - fetch from game_content
      try {
        setLoadingData(true);
        // Fetch from /api/games/:projectId to get game.content
        const response = await apiService.get(`/games/${projectId}`);
        
        if (response.data.success && response.data.data) {
          const game = response.data.data;
          const content = game.content;
          
          // Populate form with existing data from game_content
          if (content) {
            if (content.name) setTitle(content.name);
            if (content.description) setDescription(content.description);
            // Map tags back to categories (IDs will be converted to display names by useEffect)
            if (content.tags && Array.isArray(content.tags)) setCategories(content.tags);
            if (content.play_instructions) setPlayInstructions(content.play_instructions);
            if (content.icon_url) setIconUrl(content.icon_url);
            if (content.cover_image_url) setBannerUrl(content.cover_image_url);
            
            console.log('✅ Loaded project details from game_content:', content);
          } else {
            console.log('⚠️ No content found for project');
          }
        }
        setHasLoadedInitialData(true);
      } catch (error) {
        console.error('Failed to load project details:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (projectId) {
      loadProjectData();
    }
  }, [projectId, initialData, hasLoadedInitialData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  const handleCategoryToggle = (category: string) => {
    if (categories.includes(category)) {
      // Remove category
      setCategories(categories.filter(c => c !== category));
    } else if (categories.length < 3) {
      // Add category (max 3)
      setCategories([...categories, category]);
    }
  };

  const handleRemoveCategory = (category: string) => {
    setCategories(categories.filter(c => c !== category));
  };

  const handleIconUploaded = (result: ImageUploadResult) => {
    setIconUrl(result.url);
  };

  const handleIconRemoved = () => {
    setIconUrl(undefined);
  };

  const handleBannerUploaded = (result: ImageUploadResult) => {
    setBannerUrl(result.url);
  };

  const handleBannerRemoved = () => {
    setBannerUrl(undefined);
  };

  const handleScreenshotToggle = (index: number) => {
    const newSelected = localScreenshots.selected.includes(index)
      ? localScreenshots.selected.filter(i => i !== index)
      : [...localScreenshots.selected, index];
    
    const updated = screenshotService.moveSelectedToFront({
      all: localScreenshots.all,
      selected: newSelected
    });
    
    setLocalScreenshots(updated);
    onScreenshotsChange?.(updated);
    screenshotService.updateScreenshotsMetadata(projectId, updated);
  };

  const handleDeleteScreenshot = async (index: number) => {
    try {
      setDeletingIndex(index);
      const url = localScreenshots.all[index];
      
      // Wait a bit to show the loader
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const updated = await screenshotService.deleteScreenshot(
        url,
        projectId,
        localScreenshots
      );
      
      setLocalScreenshots(updated);
      onScreenshotsChange?.(updated);
    } catch (error) {
      console.error('Failed to delete screenshot:', error);
      showError('Failed to delete screenshot');
    } finally {
      setDeletingIndex(null);
    }
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(null), 4000);
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      console.log('🎨 Generating project details from code...');
      
      // Pass code if available (from editor), otherwise backend will fetch it
      const generatedDetails = await aiService.generateProjectDetails(projectId, code || undefined);
      
      console.log('✅ Generated details:', generatedDetails);
      
      // Populate form fields with generated content
      setTitle(generatedDetails.title);
      setCategories(generatedDetails.categories);
      setDescription(generatedDetails.description);
      setPlayInstructions(generatedDetails.playInstructions);
      
      // Show success notification
      setSuccessMessage('Project details generated successfully! Review and save when ready.');
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
      
    } catch (error: any) {
      console.error('Failed to generate details:', error);
      
      // Handle specific error types
      if (error.response?.status === 429) {
        showError('Daily AI usage limit exceeded. Your limit will reset tomorrow.');
      } else if (error.response?.status === 400) {
        showError(error.response.data?.error || 'Invalid request. Please check your code.');
      } else {
        showError('Failed to generate details. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveWithoutValidation = async () => {
    try {
      setLoading(true);
      console.log('💾 Saving project details without validation...');
      
      const screenshotsToSave = isEditableInStudio
        ? localScreenshots.all.filter((_, i) => localScreenshots.selected.includes(i))
        : cliScreenshots;

      const formData: ProjectFormData = {
        title,
        categories,
        description,
        playInstructions,
        iconUrl,
        bannerUrl,
        screenshots: screenshotsToSave
      };

      if (onSaveWithoutValidation) {
        // Use custom handler if provided
        await onSaveWithoutValidation(formData);
      } else {
        // Default save behavior
        await projectDataService.updateProjectDetails(projectId, {
          title,
          categories,
          description,
          playInstructions,
          iconUrl,
          bannerUrl,
          screenshots: screenshotsToSave
        });
        
        onSectionComplete?.();
      }
      
      console.log('✅ Project details saved successfully (no validation)');
      
      // Show success notification (unless silent save)
      if (!silentSave) {
        setSuccessMessage('Project details saved successfully!');
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 3000);
        
        // Close modal if provided
        onClose?.();
      }
    } catch (error) {
      console.error('Failed to save project details:', error);
      showError('Failed to save project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Client-side validation (skip if disabled)
    if (!skipValidation) {
      if (!title.trim()) {
        showError('Please enter a title for your game to continue.');
        return;
      }
      
      if (categories.length === 0) {
        showError('Please select at least one category from the dropdown to continue.');
        return;
      }
      
      if (!description.trim()) {
        showError('Please add a description for your game to continue.');
        return;
      }
      
      if (!playInstructions.trim()) {
        showError('Please add play instructions to explain how to play your game.');
        return;
      }
      
      if (!iconUrl) {
        showError('Please upload an icon for your game.');
        return;
      }
      
      // Validate screenshots based on project type
      const finalScreenshots = isEditableInStudio
        ? localScreenshots.all.filter((_, i) => localScreenshots.selected.includes(i))
        : cliScreenshots;
        
    }
    
    try {
      setLoading(true);
      console.log('💾 Saving project details...', { title, categories, description, playInstructions, iconUrl, bannerUrl });
      
      const screenshotsToSave = isEditableInStudio
        ? localScreenshots.all.filter((_, i) => localScreenshots.selected.includes(i))
        : cliScreenshots;

      const formData: ProjectFormData = {
        title,
        categories,
        description,
        playInstructions,
        iconUrl,
        bannerUrl,
        screenshots: screenshotsToSave
      };

      if (customSave) {
        // Modal mode - use custom save handler
        await customSave(formData);
      } else {
        // Standalone editor mode - use projectDataService
        await projectDataService.updateProjectDetails(projectId, {
          title,
          categories,
          description,
          playInstructions,
          iconUrl,
          bannerUrl,
          screenshots: screenshotsToSave
        });
        
        // Notify parent to refetch project data (for completion status update)
        onSectionComplete?.();
      }
      
      console.log('✅ Project details saved successfully');
      
      // Show success notification
      setSuccessMessage('Project details saved successfully!');
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
      
      // Close modal if provided
      onClose?.();
    } catch (error) {
      console.error('Failed to save project details:', error);
      showError('Failed to save project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">Loading project details...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 relative">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 pb-0">
        {/* Image Uploads */}
        <div className="flex gap-12 mb-4">
          <div className="flex-shrink-0">
            <ImageUploadField
              label="Icon"
              type="icon"
              currentImageUrl={iconUrl}
              onImageUploaded={handleIconUploaded}
              onImageRemoved={handleIconRemoved}
              projectId={projectId}
              disabled={loading || generating}
            />
          </div>
          
          <div className="flex-1">
            <ImageUploadField
              label="Cover Image"
              type="cover"
              currentImageUrl={bannerUrl}
              onImageUploaded={handleBannerUploaded}
              onImageRemoved={handleBannerRemoved}
              projectId={projectId}
              disabled={loading || generating}
            />
          </div>
        </div>

        {/* Title and Categories - Side by Side */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-gray-900 dark:text-white font-semibold mb-1.5 text-sm">
              Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter project title..."
              maxLength={50}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {title.length}/50
            </div>
          </div>

          {/* Categories */}
          <div className="relative" ref={categoryDropdownRef}>
          <label className="block text-gray-900 dark:text-white font-semibold mb-1.5 text-sm">
            Categories <span className="text-gray-500 dark:text-gray-400 text-xs font-normal">(Select up to 3)</span>
          </label>

          {/* Dropdown Button with Selected Categories */}
          <button
            type="button"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-left text-gray-900 dark:text-white rounded-lg border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors cursor-pointer"
          >
            {categories.length === 0 ? (
              <span className="text-gray-500 dark:text-gray-400">Select categories...</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {categories.map((cat) => {
                  const IconComponent = getCategoryIcon(cat);
                  return (
                    <div
                      key={cat}
                      className="flex items-center gap-1.5 px-2 py-0.5 bg-primary-500 text-white rounded text-sm"
                    >
                      {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                      <span>{cat}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCategory(cat);
                        }}
                        className="hover:bg-primary-600 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </button>

          {/* Dropdown Menu */}
          {showCategoryDropdown && (
            <div className="absolute z-10 mt-1 left-0 right-0 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loadingCategories ? (
                <div className="px-3 py-4 text-center text-gray-600 dark:text-gray-400 text-sm">
                  Loading categories...
                </div>
              ) : availableCategories.length === 0 ? (
                <div className="px-3 py-4 text-center text-gray-600 dark:text-gray-400 text-sm">
                  No categories available
                </div>
              ) : (
                availableCategories.map((category) => {
                  const isSelected = categories.includes(category.displayName);
                  const isDisabled = !isSelected && categories.length >= 3;
                  const IconComponent = getIconComponent(category.icon);
                  
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategoryToggle(category.displayName)}
                      disabled={isDisabled}
                      className={`w-full px-3 py-2 text-left transition-colors flex items-center gap-2 ${
                        isDisabled
                          ? 'text-gray-400 dark:text-gray-500'
                          : isSelected
                          ? 'bg-primary-500 text-white hover:bg-primary-600'
                          : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      <IconComponent className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1">{category.displayName}</span>
                      {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          )}
          </div>
        </div>

        {/* Description */}
        <div className="mb-4">
          <label htmlFor="description" className="block text-gray-900 dark:text-white font-semibold mb-1.5 text-sm">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your game..."
            maxLength={100}
            rows={2}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
            {description.length}/100
          </div>
        </div>

        {/* Screenshots */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <label className="block text-gray-900 dark:text-white font-semibold text-sm">
              Screenshots
            </label>
            <div className="relative group">
              <svg 
                className="w-4 h-4 text-gray-500 dark:text-gray-400 cursor-help" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/>
              </svg>
              <div className="absolute left-0 top-6 hidden group-hover:block z-10 w-64 p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-700 dark:text-gray-300">
                {isEditableInStudio 
                  ? 'Capture screenshots from the editor. Select up to 5 to showcase your game.'
                  : 'Upload screenshots for your CLI project. Include at least 2 high-quality screenshots.'}
              </div>
            </div>
          </div>

          {isEditableInStudio ? (
            // Studio Project: Capture-based system with selection
            <>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Capture screenshots from the editor and select up to 5 for your game page.
              </p>
              <div className="flex gap-4 overflow-x-auto pt-3 pb-2 pl-3">
                {localScreenshots.all.length === 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-gray-500 dark:text-gray-500 text-sm flex items-center gap-1 flex-wrap">
                      No screenshots yet. Go to Play tab and click the <Camera className="w-4 h-4 inline-block flex-shrink-0" /> camera button.
                    </div>
                    {onCaptureScreenshot && (
                      <div className="text-gray-500 dark:text-gray-500 text-sm flex items-center gap-1.5">
                        or
                        <button
                          onClick={onCaptureScreenshot}
                          disabled={isCapturingScreenshot}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isCapturingScreenshot ? (
                            <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera className="w-3.5 h-3.5" />
                          )}
                          {isCapturingScreenshot ? 'Capturing…' : 'Auto Capture Screenshots'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {localScreenshots.all.map((url, index) => (
                      <motion.div
                        key={url}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                        transition={{ layout: { duration: 0.3, ease: "easeInOut" } }}
                        className={`relative flex-shrink-0 w-32 h-48 rounded-lg border-2 ${
                          deletingIndex === index 
                            ? 'opacity-50' 
                            : 'opacity-100'
                        } ${
                          localScreenshots.selected.includes(index)
                            ? 'border-primary-500'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                      >
                        {/* Screenshot image - click to preview */}
                        <div
                          role="button"
                          tabIndex={deletingIndex === index ? -1 : 0}
                          onClick={() => deletingIndex !== index && setPreviewScreenshot(url)}
                          onKeyDown={(e) => e.key === 'Enter' && deletingIndex !== index && setPreviewScreenshot(url)}
                          className="w-full h-full cursor-pointer"
                        >
                          <img 
                            src={url} 
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-full object-cover rounded-md"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        
                        {/* Deleting loader overlay */}
                        {deletingIndex === index && (
                          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded-lg">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                              <span className="text-white text-xs font-medium">Deleting...</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Checkbox - click to select */}
                        {/* Checkbox with larger click area (30% width, 25% height from top-left) */}
                        <button
                          type="button"
                          onClick={() => handleScreenshotToggle(index)}
                          disabled={deletingIndex === index}
                          className="absolute top-0 left-0 w-[30%] h-[25%] group z-10"
                        >
                          <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all shadow-lg ${
                            localScreenshots.selected.includes(index)
                              ? 'bg-primary-500 border-primary-500'
                              : 'bg-gray-200 dark:bg-gray-700 border-gray-400 dark:border-gray-500 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
                          } ${deletingIndex === index ? 'opacity-0' : 'opacity-100'}`}>
                            {localScreenshots.selected.includes(index) && (
                              <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                            )}
                          </div>
                        </button>
                        
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteScreenshot(index);
                          }}
                          disabled={deletingIndex === index}
                          className={`absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded shadow-lg hover:bg-red-600 transition-all ${
                            deletingIndex === index ? 'opacity-0' : 'opacity-100'
                          }`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {localScreenshots.selected.length} of {localScreenshots.all.length} selected
              </div>
            </>
          ) : (
            // CLI Project: Manual upload system
            <>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                Upload screenshots for your game (PNG, JPG). Minimum 2, maximum 10 screenshots.
              </p>
              <ScreenshotUploadZone
                projectId={projectId}
                screenshots={cliScreenshots}
                onScreenshotsChange={setCliScreenshots}
                maxScreenshots={10}
                minScreenshots={2}
                disabled={loading || generating}
              />
            </>
          )}
        </div>

        {/* Play Instructions */}
        <div className="mb-4">
          <label htmlFor="playInstructions" className="block text-gray-900 dark:text-white font-semibold mb-1.5 text-sm">
            Play Instructions
          </label>
          <textarea
            id="playInstructions"
            value={playInstructions}
            onChange={(e) => setPlayInstructions(e.target.value)}
            placeholder="How do players play your game?"
            maxLength={300}
            rows={3}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
            {playInstructions.length}/300
          </div>
        </div>
        </div>
      </div>

      {/* Action Buttons - Sticky Footer */}
      {!hideButtons && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <div className="max-w-4xl mx-auto flex justify-end gap-3">
            {showGenerateButton && (
              <button
                onClick={handleGenerate}
                disabled={generating || loading}
                className={`px-6 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2 border ${
                  generating || loading
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Sparkles className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generating...' : 'Generate'}
              </button>
            )}
            {showSaveButton && (
              <button
                onClick={handleSaveWithoutValidation}
                disabled={loading || generating}
                className={`px-6 py-2 font-semibold rounded-lg transition-colors border ${
                  loading || generating
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={loading || generating}
              className={`px-6 py-2 font-semibold rounded-lg transition-colors ${
                loading || generating
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-700'
                  : 'text-white hover:opacity-90'
              }`}
              style={loading || generating ? {} : { background: FILL_GRADIENT }}
            >
              {loading ? 'Saving...' : saveButtonText}
            </button>
          </div>
        </div>
      )}

      {/* Success Notification */}
        {showSuccessNotification && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out max-w-2xl">
            <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Screenshot Preview Modal */}
        {previewScreenshot && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setPreviewScreenshot(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] p-4">
              <button
                onClick={() => setPreviewScreenshot(null)}
                className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            <img 
              src={previewScreenshot} 
              alt="Screenshot preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              loading="eager"
              decoding="sync"
            />
            </div>
          </div>
        )}

        {/* Error Notification */}
        {errorMessage && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out max-w-2xl">
            <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="font-medium">{errorMessage}</span>
            </div>
          </div>
        )}
    </div>
  );
});

export default ProjectDetailsForm;

